import React, { useState, useContext, useMemo } from 'react';

import Axios from 'axios';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { forceReloadInvoice } from '@hooks/API/UseInvoices';
import useDepartments from '@hooks/CachedLists/UseDepartments';

import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWSelect } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const ChangeDepartmentDrawer = ({
    invoiceID,
    currentDepartment,
    availableDepartments,
    onClose,
    onDepartmentChanged,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const departments = useDepartments();

    const departmentOptions = useMemo(() => {
        return departments
            .filter((department) => availableDepartments.includes(department.id))
            .map((department) => ({
                label: department.name,
                value: department.id,
            }));
    }, [departments, availableDepartments]);

    const initialValues = {
        DepartmentCreated: currentDepartment || '',
    };

    const handleSubmit = async (values) => {
        setIsSubmitting(true);
        setError(null);

        try {
            await Axios.put(`/Invoices/ChangeDepartment`, {
                InvoiceID: invoiceID,
                DepartmentID: values.DepartmentCreated,
            });

            forceReloadInvoice(invoiceID);
            onDepartmentChanged();
        } catch (err) {
            setError(`Error changing department: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <NwDrawer onClose={onClose} title="Change Department">
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={Yup.object().shape({
                    DepartmentCreated: Yup.string().required('Department is required').min(1, 'Department is required'),
                })}
            >
                {({ isSubmitting: formikSubmitting, values, handleSubmit }) => (
                    <NwForm values={values} onFinish={handleSubmit}>
                        <Field
                            component={NWSelect}
                            label="Department"
                            name="DepartmentCreated"
                            options={departmentOptions}
                            value={values.DepartmentCreated}
                            extra="Select the department for this invoice"
                        />

                        {error && <div style={{ color: 'red', margin: '1rem 0' }}>{error}</div>}

                        <NwFormButtonsBar
                            marginTop="2rem"
                            left={<NwCancelButton disabled={isSubmitting} onClick={onClose} />}
                            right={<NwSaveButton htmlType="submit" loading={isSubmitting} />}
                        />
                    </NwForm>
                )}
            </Formik>
        </NwDrawer>
    );
};

export default ChangeDepartmentDrawer;
